/**
 * SLEEPSAKE — Seamless slideshow custom element
 *
 * Extends Horizon's `<slideshow-component>` with true infinite-loop carousel
 * behavior using a clone + scroll-driven recenter strategy (the same pattern
 * Swiper, Embla, and Splide use). Replaces Horizon's stock "snap-back across
 * the entire scroller" wrap animation with a seamless visual loop that works
 * uniformly for desktop arrow clicks, mobile drag, and autoplay.
 *
 * Strategy
 * --------
 *  1. On init, clone every real slide on each side of the scroller (head +
 *     tail). Clones share visual content with their real counterparts but
 *     are skipped by `refs[]`, dot pagination, and the IntersectionObserver
 *     (we strip the `ref`, `slide-id`, and `id` attributes).
 *  2. Anchor the initial scroll position at the first real slide so the user
 *     never sees clones until they scroll past a real-slide boundary.
 *  3. Listen on raw `scroll` events from `<slideshow-slides>`. Debounce
 *     ~80ms past the last event ("scroll stable"). When stable, if the
 *     scroll position has crossed into clone territory on either side,
 *     silently shift `scrollLeft` by `realRegionWidth` so the visible
 *     content is identical but the user is now positioned in the real-slide
 *     region.
 *  4. Override `next()` / `previous()` so a wrap-click smooth-scrolls
 *     FORWARD into head clones (or BACKWARD into tail clones) instead of
 *     stock Horizon scrolling backward across the entire scroller. The
 *     scroll-driven recenter then handles the silent jump after the smooth
 *     scroll settles.
 *  5. Override `select()` so non-adjacent dot jumps (mobile dot 1 → dot 5)
 *     bypass Horizon's placeholder-reorder branch (which would yank a real
 *     slide across the clone boundary and silently invalidate our offset
 *     cache). Non-instant, non-adjacent jumps smooth-scroll directly to the
 *     target real slide's offset; everything else falls through to super.
 *  6. On mobile, block Horizon's manual mousedown drag handler so native
 *     scroll-snap takes over. Drag past either boundary works the same way
 *     as desktop arrow clicks: native scroll into clones → scroll-stable →
 *     silent shift to equivalent real slide.
 *  7. Stride for the wrap check is read from the `--carousel-columns` /
 *     `--carousel-mobile-columns` CSS custom properties (recomputed on
 *     resize). `visibleSlides.length` is unreliable immediately after a
 *     silent shift because the IntersectionObserver hasn't fired yet,
 *     which was the root cause of the prior "loops 3 times then stuck"
 *     bug — using a layout-cached stride avoids that race entirely.
 *  8. `updatedCallback` (Section Rendering API morph) tears down all
 *     state and re-runs `_initSeamless` after a frame so theme-editor
 *     setting changes don't wedge the carousel against stale offsets.
 *
 * Used by `sections/customer-reviews-carousel.liquid`. Self-defines
 * `<seamless-slideshow-component>` so the section template is the only
 * thing that has to opt in.
 */

(function () {
  if (customElements.get("seamless-slideshow-component")) return;

  const DESKTOP_QUERY = "(min-width: 750px)";
  // Scroll-debounce window for the raw scroll listener — long enough for
  // browser scroll-snap to settle, short enough to feel instant.
  const RECENTER_DEBOUNCE_MS = 80;
  // Pointer-release safety timeout — covers iOS momentum scroll which can
  // settle 150–300ms past the last `scroll` event. Used as a fallback for
  // browsers that don't support the `scrollend` event.
  const POINTER_END_TIMEOUT_MS = 300;
  // Sub-pixel tolerance for boundary checks. Browsers round scrollLeft to
  // device pixels (sometimes fractional on hi-DPI), so an exact equality
  // would oscillate near the threshold.
  const POSITION_EPSILON = 1;
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  // SLEEPSAKE: how many slides each next/previous click advances by.
  // Hardcoded to 1 because reviews are read-one-at-a-time content (each
  // card is a unit of meaning — one customer's testimonial). Stock
  // Horizon's default of "stride = visible columns" treats the carousel
  // as a paginated grid, which makes sense for product carousels but
  // not for review content. Soft-hardcoded as a constant rather than a
  // schema setting so merchants don't have to think about it; flip to
  // another value here if a future use case ever needs page-stride.
  const STRIDE_PER_CLICK = 1;

  customElements.whenDefined("slideshow-component").then(() => {
    const Slideshow = customElements.get("slideshow-component");
    if (!Slideshow) return;
    if (customElements.get("seamless-slideshow-component")) return;

    class SeamlessSlideshow extends Slideshow {
      /** @type {HTMLElement[]} */
      _headClones = [];
      /** @type {HTMLElement[]} */
      _tailClones = [];
      _seamlessReady = false;
      _recentering = false;
      _pointerActive = false;
      /** Cached `(min-width: 750px)` match. Refreshed on `change`. */
      _isDesktop = false;
      /** Layout-cached desktop or mobile column count, used as wrap stride. */
      _columns = 1;
      _paddingStart = 0;
      _realFirstOffset = 0;
      _headFirstOffset = 0;
      _realRegionWidth = 0;
      /** @type {number | undefined} */
      _recenterTimer;
      /** @type {(() => void) | null} */
      _pendingScrollEnd = null;
      /** @type {ResizeObserver | undefined} */
      _resizeObserver;
      /** @type {MediaQueryList | undefined} */
      _mqList;

      async connectedCallback() {
        await super.connectedCallback();
        // NOTE: `super.connectedCallback()` only awaits the in-progress
        // view transition, NOT Horizon's deferred `#setupSlideshow` work.
        // That setup is fire-and-forget inside `scheduler.schedule(...)`
        // and runs an inner `requestAnimationFrame` for the initial
        // `select()`. One macrotask (`setTimeout(0)`) plus two rAFs
        // gives the chain enough room to flush in practice without a
        // perceptible delay. If init still races, `_initSeamless()`'s
        // guarded early-return logs to the console so the failure
        // mode is visible (see `_initSeamless`).
        await new Promise((r) => setTimeout(r, 0));
        await new Promise((r) => requestAnimationFrame(r));
        await new Promise((r) => requestAnimationFrame(r));
        if (!this.isConnected) return;
        this._initSeamless();
      }

      /**
       * Section Rendering API re-renders this element in place when a
       * merchant edits a theme-editor setting. Stock Horizon's morph
       * removes our cloned slides and refreshes `refs.slides[]` with the
       * new real slides — but our cached offsets, flags, and arrays
       * survive, so subsequent next()/previous() calls would scrollTo
       * stale positions and wedge the carousel.
       */
      updatedCallback() {
        super.updatedCallback();
        this._teardownSeamless();
        // Wait one frame so morph + Component#updateRefs have settled
        // before re-cloning slides.
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this._initSeamless();
        });
      }

      disconnectedCallback() {
        // Cleanup our state FIRST so a throw in `super.disconnectedCallback()`
        // doesn't leak document-level listeners.
        try {
          this._teardownSeamless();
        } catch (err) {
          console.warn("[SeamlessSlideshow] teardown error", err);
        }
        super.disconnectedCallback();
      }

      /**
       * Idempotent teardown — safe to call from both `disconnectedCallback`
       * and `updatedCallback`. Removes any clones we added, disconnects
       * observers, removes listeners, clears timers, resets flags.
       */
      _teardownSeamless() {
        const scroller = this.refs?.scroller;
        if (scroller) {
          try {
            scroller.removeEventListener("scroll", this._onScroll);
            scroller.removeEventListener("mousedown", this._mobileBlockMousedown, { capture: true });
            scroller.removeEventListener("pointerdown", this._handlePointerDown);
            if (this._pendingScrollEnd) {
              scroller.removeEventListener("scrollend", this._pendingScrollEnd);
            }
          } catch (err) {
            console.warn("[SeamlessSlideshow] scroller listener removal", err);
          }
        }
        try {
          document.removeEventListener("pointerup", this._handlePointerEnd);
          document.removeEventListener("pointercancel", this._handlePointerEnd);
        } catch (err) {
          console.warn("[SeamlessSlideshow] document listener removal", err);
        }
        this._pendingScrollEnd = null;
        this._resizeObserver?.disconnect();
        this._resizeObserver = undefined;
        this._mqList?.removeEventListener("change", this._onMediaChange);
        this._mqList = undefined;
        if (this._recenterTimer != null) {
          clearTimeout(this._recenterTimer);
          this._recenterTimer = undefined;
        }
        // Defensively remove any clone DOM still attached. Morph usually
        // strips them (since they aren't in the server HTML), but if the
        // morph algorithm ever preserves them we'd double-clone on re-init.
        for (const clone of this._headClones) clone.remove();
        for (const clone of this._tailClones) clone.remove();
        this._headClones = [];
        this._tailClones = [];
        this._seamlessReady = false;
        this._recentering = false;
        this._pointerActive = false;
      }

      _initSeamless() {
        // Don't add clones if looping is disabled — in that case the section
        // should behave like stock Horizon (clamped, no wrap).
        if (!this.infinite) return;

        if (!this.isConnected) {
          console.warn("[SeamlessSlideshow] init skipped — not connected");
          return;
        }
        const scroller = this.refs?.scroller;
        const realSlides = this.refs?.slides;
        if (!scroller) {
          console.warn("[SeamlessSlideshow] init skipped — refs.scroller missing");
          return;
        }
        if (!realSlides || realSlides.length < 2) return;

        // Skip clone insertion when there's no overflow at all — the
        // controls auto-hide and there's nothing to loop. Without this
        // guard, adding clones would balloon scrollWidth past offsetWidth
        // and suppress the auto-hide in `slideshow.js`.
        if (scroller.scrollWidth <= scroller.offsetWidth) return;

        const tailClones = realSlides.map((slide) => this._buildClone(slide, "tail"));
        const headClones = realSlides.map((slide) => this._buildClone(slide, "head"));

        scroller.prepend(...tailClones);
        scroller.append(...headClones);

        this._tailClones = tailClones;
        this._headClones = headClones;
        this._seamlessReady = true;

        // Cache breakpoint match BEFORE first column read.
        this._mqList = window.matchMedia(DESKTOP_QUERY);
        this._isDesktop = this._mqList.matches;

        this._recomputeMetrics({ reanchor: false });

        // Anchor at the slide matching `this.current` (preserves position
        // across Section Rendering API morphs in the theme editor; on
        // first mount `current` defaults to `initial-slide` which is 0).
        // Suppress the recenter listener for this assignment so the
        // resulting scroll event doesn't kick off a redundant recenter
        // check at startup. Also override CSS `scroll-behavior: smooth`
        // (set in `slideshow-styles.liquid`) so the anchor is an
        // instantaneous jump, not an animated slide-in.
        const anchorIdx = Math.max(0, Math.min(realSlides.length - 1, this.current ?? 0));
        const anchorSlide = realSlides[anchorIdx] ?? realSlides[0];
        this._anchorScrollInstantly(anchorSlide.offsetLeft - this._paddingStart);

        scroller.addEventListener("scroll", this._onScroll, { passive: true });

        // Pointer tracking — we don't want to silent-shift while the user
        // has a finger on the screen mid-drag, even if they momentarily
        // hold still long enough for the debounce to fire.
        scroller.addEventListener("pointerdown", this._handlePointerDown);
        document.addEventListener("pointerup", this._handlePointerEnd);
        document.addEventListener("pointercancel", this._handlePointerEnd);

        // Block Horizon's manual mousedown drag on mobile. Native
        // scroll-snap drag handles wraps via the recenter loop instead;
        // Horizon's manual drag would otherwise rubber-band back to a real
        // slide on `pointerup` and break the seamless illusion.
        scroller.addEventListener("mousedown", this._mobileBlockMousedown, { capture: true });

        this._resizeObserver = new ResizeObserver(() => this._recomputeMetrics({ reanchor: true }));
        this._resizeObserver.observe(scroller);

        this._mqList.addEventListener("change", this._onMediaChange);
      }

      /**
       * Instantly set scrollLeft, suppressing CSS `scroll-behavior: smooth`
       * for the assignment. Used by both initial anchor and the resize
       * re-anchor path. Caller is responsible for flag bookkeeping
       * around `_recentering`.
       *
       * @param {number} target
       */
      _anchorScrollInstantly(target) {
        const scroller = this.refs?.scroller;
        if (!scroller) return;
        this._recentering = true;
        const prevBehavior = scroller.style.scrollBehavior;
        scroller.style.scrollBehavior = "auto";
        scroller.scrollLeft = target;
        // Force layout flush so the new scroll position commits to the same
        // paint frame before we restore smooth behavior.
        void scroller.offsetWidth;
        scroller.style.scrollBehavior = prevBehavior;
        // Two rAFs: covers Safari's slightly-delayed scroll dispatch after
        // a synchronous scrollLeft assignment.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._recentering = false;
          });
        });
      }

      /**
       * @param {HTMLElement} slide
       * @param {'head'|'tail'} kind
       */
      _buildClone(slide, kind) {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("data-clone", kind);
        clone.setAttribute("tabindex", "-1");
        clone.removeAttribute("slide-id");
        // Strip refs so Component's MutationObserver doesn't add the clone
        // to refs.slides[]. This keeps dot pagination, IntersectionObserver,
        // and `visibleSlides` tracking the real slides only.
        clone.removeAttribute("ref");
        clone.querySelectorAll("[ref]").forEach((el) => el.removeAttribute("ref"));
        // Strip any [id] from the clone subtree so we don't produce
        // duplicate DOM ids (defensive — current card has none, but
        // future schema additions might add an id-bearing element).
        if (clone.id) clone.removeAttribute("id");
        clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
        // Yank focusable descendants out of the tab order. The current card
        // has none, but future schema additions might.
        clone.querySelectorAll("a,button,input,select,textarea,[tabindex]").forEach((el) => {
          el.setAttribute("tabindex", "-1");
          el.setAttribute("aria-hidden", "true");
        });
        return clone;
      }

      /**
       * @param {{ reanchor?: boolean }} [opts]
       */
      _recomputeMetrics(opts = {}) {
        if (!this._seamlessReady) return;
        const scroller = this.refs?.scroller;
        const realSlides = this.refs?.slides;
        if (!scroller || !realSlides || !realSlides.length) return;
        const headFirst = this._headClones[0];
        const realFirst = realSlides[0];
        if (!headFirst || !realFirst) return;
        this._paddingStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
        this._realFirstOffset = realFirst.offsetLeft;
        this._headFirstOffset = headFirst.offsetLeft;
        this._realRegionWidth = headFirst.offsetLeft - realFirst.offsetLeft;
        this._updateColumns();

        // Re-anchor on resize so the visible card stays aligned to the
        // current `current` index after a viewport rotation. Skip while
        // the user is interacting or we're already recentering — the
        // existing scroll/pointer flows will reach a consistent state on
        // their own.
        if (!opts.reanchor) return;
        if (this._recentering || this._pointerActive) return;
        if (this.hasAttribute("dragging")) return;
        const currentIdx = Math.max(0, Math.min(realSlides.length - 1, this.current ?? 0));
        const target = realSlides[currentIdx].offsetLeft - this._paddingStart;
        if (Math.abs(scroller.scrollLeft - target) <= POSITION_EPSILON) return;
        this._anchorScrollInstantly(target);
      }

      _updateColumns() {
        const propName = this._isDesktop ? "--carousel-columns" : "--carousel-mobile-columns";
        const raw = getComputedStyle(this).getPropertyValue(propName).trim();
        const parsed = parseInt(raw, 10);
        this._columns = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      /** @param {MediaQueryListEvent} event */
      _onMediaChange = (event) => {
        this._isDesktop = event.matches;
        this._updateColumns();
      };

      _handlePointerDown = () => {
        this._pointerActive = true;
      };

      _handlePointerEnd = () => {
        this._pointerActive = false;
        this._scheduleRecenterAfterScrollSettle();
      };

      /**
       * After a pointer release, scroll-snap may keep settling for
       * 150–300ms on iOS momentum scroll. Wait for the `scrollend` event
       * (Chrome 114+, Safari 18.1+) when available; otherwise fall back to
       * a 300ms timeout. Whichever fires first triggers `_recenterIfNeeded`
       * once.
       */
      _scheduleRecenterAfterScrollSettle = () => {
        const scroller = this.refs?.scroller;
        if (this._recenterTimer != null) {
          clearTimeout(this._recenterTimer);
          this._recenterTimer = undefined;
        }
        if (this._pendingScrollEnd && scroller) {
          scroller.removeEventListener("scrollend", this._pendingScrollEnd);
        }
        this._pendingScrollEnd = null;

        const fire = () => {
          if (this._recenterTimer != null) {
            clearTimeout(this._recenterTimer);
            this._recenterTimer = undefined;
          }
          if (this._pendingScrollEnd && scroller) {
            scroller.removeEventListener("scrollend", this._pendingScrollEnd);
            this._pendingScrollEnd = null;
          }
          this._recenterIfNeeded();
        };

        this._recenterTimer = window.setTimeout(fire, POINTER_END_TIMEOUT_MS);
        if (scroller && "onscrollend" in scroller) {
          this._pendingScrollEnd = fire;
          scroller.addEventListener("scrollend", fire);
        }
      };

      /** @param {Event} event */
      _mobileBlockMousedown = (event) => {
        if (this._isDesktop) return;
        // Capture-phase + stopImmediatePropagation pre-empts Slideshow's
        // bubble-phase mousedown listener on the same element without
        // affecting click/tap events that follow.
        event.stopImmediatePropagation();
      };

      _onScroll = () => {
        if (this._recentering) return;
        if (this._recenterTimer != null) clearTimeout(this._recenterTimer);
        this._recenterTimer = window.setTimeout(this._recenterIfNeeded, RECENTER_DEBOUNCE_MS);
      };

      _recenterIfNeeded = () => {
        if (this._recentering || !this._seamlessReady) return;
        // Don't shift while the user has a finger/mouse down — could yank
        // the scroll position out from under an in-progress drag.
        if (this._pointerActive) return;
        // Don't shift while Horizon's manual drag is in progress (desktop).
        if (this.hasAttribute("dragging")) return;

        const scroller = this.refs?.scroller;
        if (!scroller) return;
        const realRegionWidth = this._realRegionWidth;
        if (realRegionWidth <= 0) return;

        const scrollLeft = scroller.scrollLeft;
        const padStart = this._paddingStart;
        // `headThreshold`: at/past this scrollLeft we've crossed into the
        // first head clone (i.e. user has scrolled entirely out of real
        // territory at the END of the strip).
        const headThreshold = this._headFirstOffset - padStart - POSITION_EPSILON;
        // `realStartThreshold`: below this scrollLeft we're in tail-clone
        // territory (i.e. before the first real slide). Named for its role
        // as the "start of real territory" marker.
        const realStartThreshold = this._realFirstOffset - padStart - POSITION_EPSILON;

        let target = null;
        if (scrollLeft >= headThreshold) {
          // Past the start of the first head clone → user has scrolled
          // entirely out of real territory. Jump backward by one full
          // real-region width to land on the equivalent real slide.
          target = scrollLeft - realRegionWidth;
        } else if (scrollLeft < realStartThreshold) {
          // Before the start of the first real slide → in tail-clone
          // territory. Jump forward by one real-region width.
          target = scrollLeft + realRegionWidth;
        }
        if (target == null) return;

        this._silentShift(target);
      };

      /** @param {number} targetScroll */
      _silentShift(targetScroll) {
        const scroller = this.refs?.scroller;
        if (!scroller) return;
        this._anchorScrollInstantly(targetScroll);
        // Update `current` to match the new visible slide. The setter
        // writes dot/aria-selected; Slideshow's `#sync()` may also run
        // later via scroll events but will agree with us once the
        // IntersectionObserver catches up to the new positions.
        this._syncCurrentFromScroll(targetScroll);
      }

      /** @param {number} scrollLeft */
      _syncCurrentFromScroll(scrollLeft) {
        const realSlides = this.refs?.slides;
        if (!realSlides || !realSlides.length) return;
        const padStart = this._paddingStart;
        let closestIdx = 0;
        let minDist = Infinity;
        for (let i = 0; i < realSlides.length; i++) {
          const dist = Math.abs(realSlides[i].offsetLeft - padStart - scrollLeft);
          if (dist < minDist) {
            minDist = dist;
            closestIdx = i;
          }
        }
        if (this.current !== closestIdx) {
          this.current = closestIdx;
        }
      }

      _isSeamlessActive() {
        return this._seamlessReady && this.infinite;
      }

      /**
       * Override stock `select()` to bypass the placeholder-reorder branch
       * for non-adjacent jumps **on mobile only** when seamless is active.
       *
       * Stock Horizon's reorder branch (`slideshow.js` ~L248) only fires
       * when `visibleSlides.length === 1` — i.e. the mobile single-card-
       * visible case. On mobile, that branch reorders DOM across our
       * clone boundary, yanks a real slide out of position, and silently
       * invalidates `_realFirstOffset`/`_headFirstOffset`. So we
       * intercept it.
       *
       * On desktop (multi-card visible), stock `super.select()` is the
       * correct path: it routes through `Scroller#scroll()` which sets
       * `#userEvent = false`, suppressing intermediate `current` updates
       * during the smooth scroll. Skipping `Scroller` would cause dot
       * pagination to flicker through every intermediate slide before
       * settling — exactly the UX problem stock engineered around.
       *
       * Strategy:
       *   - instant, adjacent, OR desktop → fall through to super.select
       *   - mobile + non-adjacent + animated → smooth-scroll directly to
       *     the real slide's offset; recenter handles the rest
       *
       * @param {number|string|{id: string}} input
       * @param {Event} [event]
       * @param {{ animate?: boolean }} [options]
       */
      async select(input, event, options = {}) {
        if (!this._isSeamlessActive()) return super.select(input, event, options);
        const realSlides = this.refs?.slides;
        const slides = this.slides;
        if (!realSlides?.length || !slides?.length) {
          return super.select(input, event, options);
        }

        // Resolve target index against `slides` (Horizon filters out hidden
        // slides; we mirror that behavior so id-based input still resolves).
        let requestedIndex;
        if (typeof input === "number") {
          requestedIndex = input;
        } else if (typeof input === "string") {
          requestedIndex = parseInt(input, 10);
        } else if (input && typeof input === "object" && "id" in input) {
          const target = realSlides.find((s) => s.getAttribute("slide-id") == input.id);
          requestedIndex = target ? slides.indexOf(target) : NaN;
        } else {
          requestedIndex = NaN;
        }
        if (!Number.isFinite(requestedIndex)) {
          return super.select(input, event, options);
        }

        const lastIndex = slides.length - 1;
        let targetIndex = requestedIndex;
        if (this.infinite) {
          if (requestedIndex < 0) targetIndex = lastIndex;
          else if (requestedIndex > lastIndex) targetIndex = 0;
        } else {
          targetIndex = Math.max(0, Math.min(lastIndex, requestedIndex));
        }

        const animate = options.animate !== false;
        const reduced = window.matchMedia(REDUCED_MOTION_QUERY).matches;
        const instant = !animate || reduced;
        const isAdjacent = Math.abs(targetIndex - this.current) <= 1;
        // SLEEPSAKE: only mobile triggers stock's `visibleSlides.length === 1`
        // placeholder-reorder branch. Desktop should fall through to super.
        const isMobileSingleVisible = (this.visibleSlides?.length ?? 0) <= 1;

        if (instant || isAdjacent || !isMobileSingleVisible) {
          return super.select(input, event, options);
        }

        const targetSlide = realSlides[targetIndex];
        const scroller = this.refs?.scroller;
        if (!targetSlide || !scroller) {
          return super.select(input, event, options);
        }

        event?.preventDefault();
        const targetScroll = targetSlide.offsetLeft - this._paddingStart;
        scroller.scrollTo({ left: targetScroll, behavior: "smooth" });
        // Update `current` immediately so dot pagination tracks user intent
        // during the smooth scroll. The recenter loop will reaffirm the
        // same value when scroll settles — idempotent.
        this.current = targetIndex;
      }

      next(event, options) {
        if (!this._isSeamlessActive()) return super.next(event, options);
        const slides = this.slides;
        if (!slides || slides.length === 0) return super.next(event, options);

        const lastIdx = slides.length - 1;

        if (this.current + STRIDE_PER_CLICK > lastIdx) {
          // Wrap forward: smooth-scroll INTO head clones rather than
          // letting `super.select(0)` scroll backward across the entire
          // scroller. The scroll-driven recenter handles the silent shift
          // back to the real first slide once the smooth scroll settles.
          event?.preventDefault();
          this._wrapForward();
          return;
        }
        // Stock `super.next()` would advance by `visibleSlides.length`
        // (page stride — wrong for review content). Route directly to
        // `super.select(current + 1, ...)` so we get the correct stride
        // AND keep stock Horizon's `Scroller#scroll()` path with its
        // user-event suppression — no dot-pagination flicker.
        event?.preventDefault();
        return super.select(this.current + STRIDE_PER_CLICK, event, options);
      }

      previous(event, options) {
        if (!this._isSeamlessActive()) return super.previous(event, options);
        const slides = this.slides;
        if (!slides || slides.length === 0) return super.previous(event, options);

        if (this.current - STRIDE_PER_CLICK < 0) {
          event?.preventDefault();
          this._wrapBackward();
          return;
        }
        event?.preventDefault();
        return super.select(this.current - STRIDE_PER_CLICK, event, options);
      }

      _wrapForward() {
        const scroller = this.refs?.scroller;
        if (!scroller) return;
        // Update `current` immediately so the dot pagination reflects the
        // user's intent during the smooth scroll, not 80ms+ after it
        // settles. The recenter loop will reaffirm the same value when
        // the scroll settles — idempotent.
        this.current = 0;
        const target = this._headFirstOffset - this._paddingStart;
        // SLEEPSAKE: honor prefers-reduced-motion the same way stock
        // Horizon's `Scroller#scroll()` does — instant scroll for users
        // who've opted out of motion. Otherwise the wrap animation
        // would jar against the snappy non-wrap arrow clicks.
        const reduced = window.matchMedia(REDUCED_MOTION_QUERY).matches;
        scroller.scrollTo({ left: target, behavior: reduced ? "auto" : "smooth" });
      }

      _wrapBackward() {
        const scroller = this.refs?.scroller;
        if (!scroller) return;
        const realSlides = this.refs?.slides;
        if (!realSlides || realSlides.length === 0) return;
        const tailClones = this._tailClones;
        if (!tailClones.length) return;

        const slideCount = realSlides.length;
        // Modular wrap. With STRIDE_PER_CLICK=1 this collapses to
        // `current === 0 ? lastIdx : current - 1`, but kept in modular
        // form so the math still works if the stride constant is ever
        // bumped to support page-mode in a future use case.
        const targetIdx = (((this.current - STRIDE_PER_CLICK) % slideCount) + slideCount) % slideCount;
        // Tail clones mirror real slides 1:1 in DOM order, so the clone
        // for real slide `targetIdx` is `tailClones[targetIdx]`.
        const tailTarget = tailClones[targetIdx];
        if (!tailTarget) return;

        this.current = targetIdx;
        const target = tailTarget.offsetLeft - this._paddingStart;
        // SLEEPSAKE: honor prefers-reduced-motion (matches `_wrapForward`).
        const reduced = window.matchMedia(REDUCED_MOTION_QUERY).matches;
        scroller.scrollTo({ left: target, behavior: reduced ? "auto" : "smooth" });
      }
    }

    if (!customElements.get("seamless-slideshow-component")) {
      customElements.define("seamless-slideshow-component", SeamlessSlideshow);
    }
  });
})();
